import { useEffect, useState } from "react";
import { Banknote, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  type ConnectAccount,
  fetchConnectAccount,
  openConnectDashboard,
  refreshConnectAccount,
  startConnectOnboarding,
} from "@/lib/affiliateConnect";

type Props = { userId: string | undefined };

export function PayoutsCard({ userId }: Props) {
  const [account, setAccount] = useState<ConnectAccount | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!userId) return;
    let mounted = true;
    fetchConnectAccount(userId)
      .then((acct) => {
        if (mounted) setAccount(acct);
      })
      .catch((e) => toast.error(e instanceof Error ? e.message : String(e)))
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [userId]);

  // If the URL has ?connect=return, refresh to pick up post-onboarding state.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.get("connect")) return;
    refreshConnectAccount()
      .then((acct) => {
        if (acct) setAccount(acct);
        toast.success("Stripe payouts updated");
      })
      .catch((e) => toast.error(e instanceof Error ? e.message : String(e)));
  }, []);

  const onStart = async () => {
    setBusy(true);
    try {
      const url = await startConnectOnboarding();
      window.location.href = url;
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };

  const onDashboard = async () => {
    setBusy(true);
    try {
      const url = await openConnectDashboard();
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="shadow-soft">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Banknote className="h-4 w-4 text-primary" />
          Payouts via Stripe
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {loading ? (
          <p className="text-muted-foreground">
            <Loader2 className="mr-2 inline h-3.5 w-3.5 animate-spin" />
            Loading…
          </p>
        ) : !account ? (
          <>
            <p className="text-muted-foreground">
              Connect a Stripe account to receive automatic payouts. Stripe handles
              identity verification, tax forms (W-9 / W-8BEN, 1099s at year-end), and
              direct deposit to your bank. Payouts are sent automatically once your
              approved balance reaches $50.
            </p>
            <Button onClick={onStart} disabled={busy}>
              {busy ? "Opening Stripe…" : "Set up Stripe payouts"}
            </Button>
          </>
        ) : !account.payouts_enabled ? (
          <>
            <p className="text-muted-foreground">
              Stripe needs more information before payouts can be sent.
              {account.requirements_currently_due.length
                ? ` Outstanding: ${account.requirements_currently_due.join(", ")}.`
                : null}
            </p>
            <Button onClick={onStart} disabled={busy} variant="default">
              {busy ? "Opening Stripe…" : "Resume setup"} <ExternalLink className="ml-1 h-3.5 w-3.5" />
            </Button>
          </>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                Payouts enabled
              </span>
              {account.country ? (
                <span className="text-xs text-muted-foreground">
                  {account.country} · {account.default_currency?.toUpperCase() ?? "—"}
                </span>
              ) : null}
            </div>
            <p className="text-muted-foreground">
              Approved commissions of $50 or more are transferred daily to your Stripe
              account. Refunds reverse pending balances; already-paid funds are never
              clawed back.
            </p>
            <Button onClick={onDashboard} disabled={busy} variant="outline" size="sm">
              Open Stripe dashboard <ExternalLink className="ml-1 h-3.5 w-3.5" />
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
